import { useCallback, useEffect, useRef } from 'react';
import { getGroupSettingsFunc, updateGroupSettingsFunc } from '../../../lib/helper';
import { GroupSettings } from '../../../types/common';
import { InvestmentThesis, Workspace } from '../../../types/files';
import ObjectUtils from '../../../utils/ObjectUtils';
import useGroupSettingsContext from '../useGroupSettingsContext';

const useGroupSettingsEffects = (userGroup: string | null) => {
  const {
    statuses,
    tags,
    stages,
    externalSync,
    autoIngestFromEmail,
    autoSendDigestEmail,
    investmentThesis,
    groupWorkspaces,
    excludedCompanyTerms,
    setGroupSettingsLoaded,
    setGroupSettings
  } = useGroupSettingsContext();

  const updatedStatusRef = useRef<string[]>([]);
  const updatedTagsRef = useRef<string[]>([]);
  const updatedStagesRef = useRef<string[]>([]);
  const updatedAutoSyncRef = useRef<boolean>(false);
  const updatedAutoIngestRef = useRef<boolean>(false);
  const updatedAutoSendDigestRef = useRef<boolean>(false);
  const updatedInvestmentThesis = useRef<InvestmentThesis|null>(null);
  const updatedGroupWorkspacesRef = useRef<Workspace[]>([]);
  const excludedCompanyTermsRef = useRef<string[]>([]);

  const refreshSettings = useCallback(() => {
    getGroupSettingsFunc({}).then((groupSettingsData?: GroupSettings) => {
      updatedTagsRef.current = [...groupSettingsData?.tags ?? []].sort((a, b) => a > b ? 1 : 0) ?? [];
      updatedStagesRef.current = [...groupSettingsData?.investmentStages ?? []].sort((a, b) => a > b ? 1 : 0) ?? [];
      updatedStatusRef.current = groupSettingsData?.statuses?.map(statuses => !!statuses ? statuses.trim().toLowerCase() : '<rename this statuses>') ?? [];
      updatedAutoSyncRef.current = groupSettingsData?.shouldSyncAllDashboardsExternally ?? false;
      updatedAutoIngestRef.current = groupSettingsData?.shouldIngestDashboardsFromEmail ?? false;
      updatedAutoSendDigestRef.current = groupSettingsData?.shouldSendDigestEmail ?? false;
      updatedInvestmentThesis.current = groupSettingsData?.investmentThesis ?? null;
      updatedGroupWorkspacesRef.current = groupSettingsData?.workspaces ?? [];
      excludedCompanyTermsRef.current = groupSettingsData?.excludedCompanyTerms ?? [];

      setGroupSettings(groupSettingsData!);
      setGroupSettingsLoaded(true);
    });
  }, [setGroupSettings, setGroupSettingsLoaded]);

  useEffect(() => {
    if (userGroup) {
      setGroupSettingsLoaded(false);
      refreshSettings();
    }
  // eslint-disable-next-line
  }, [userGroup]);

  useEffect(() => {
    if (userGroup && !!statuses.length) {
      if (!ObjectUtils.equalObjects(statuses, updatedStatusRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, statuses }));
        updatedStatusRef.current = structuredClone(statuses ?? []);
      }
    }
  // eslint-disable-next-line
  }, [statuses]);

  useEffect(() => {
    if (userGroup && !!tags.length) {
      if (!ObjectUtils.equalObjects(tags, updatedTagsRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, tags }));
        updatedTagsRef.current = structuredClone(tags ?? []);
      }
    }
  // eslint-disable-next-line
  }, [tags]);

  useEffect(() => {
    if (userGroup && !!stages.length) {
      if (!ObjectUtils.equalObjects(stages, updatedStagesRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, investmentStages: stages, }));
        updatedStagesRef.current = structuredClone(stages || []);
      }
    }
  // eslint-disable-next-line
  }, [stages]);

  useEffect(() => {
    if (userGroup) {
      if (!ObjectUtils.equalObjects(externalSync, updatedAutoSyncRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, shouldSyncAllDashboardsExternally: externalSync, }));
        updatedAutoSyncRef.current = structuredClone(externalSync || false);
      }
    }
  // eslint-disable-next-line
  }, [externalSync]);

  useEffect(() => {
    if (userGroup) {
      if (!ObjectUtils.equalObjects(autoIngestFromEmail, updatedAutoIngestRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, shouldIngestDashboardsFromEmail: autoIngestFromEmail, }));
        updatedAutoIngestRef.current = structuredClone(autoIngestFromEmail || false);
      }
    }
  // eslint-disable-next-line
  }, [autoIngestFromEmail]);

  useEffect(() => {
    if (userGroup) {
      if (!ObjectUtils.equalObjects(autoSendDigestEmail, updatedAutoSendDigestRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, shouldSendDigestEmail: autoSendDigestEmail, }));
        updatedAutoSendDigestRef.current = structuredClone(autoSendDigestEmail || false);
      }
    }
  // eslint-disable-next-line
  }, [autoSendDigestEmail]);

  useEffect(() => {
    if (userGroup && !!investmentThesis) {
      if (!ObjectUtils.equalObjects(investmentThesis, updatedInvestmentThesis.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, investmentThesis: investmentThesis!, }));
        updatedInvestmentThesis.current = structuredClone(investmentThesis ?? null);
      }
    }
  // eslint-disable-next-line
  }, [investmentThesis]);

  useEffect(() => {
    if (userGroup && !!groupWorkspaces.length) {
      if (!ObjectUtils.equalObjects(groupWorkspaces, updatedGroupWorkspacesRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, workspaces: groupWorkspaces, }));
        updatedGroupWorkspacesRef.current = structuredClone(groupWorkspaces || []);
      }
    }
  // eslint-disable-next-line
  }, [groupWorkspaces]);

  useEffect(() => {
    if (userGroup) {
      if (!ObjectUtils.equalObjects(excludedCompanyTerms, excludedCompanyTermsRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group: userGroup, excludedCompanyTerms: excludedCompanyTerms, }));
        excludedCompanyTermsRef.current = structuredClone(excludedCompanyTerms ?? []);
      }
    }
  // eslint-disable-next-line
  }, [excludedCompanyTerms]);

  return { refreshSettings };
};

export default useGroupSettingsEffects;